import React, { useState, useEffect, useMemo } from "react";
import {
    Box,
    Grid,
    Paper,
    Typography,
    Button,
    Stack,
    Alert,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Card,
    CardContent,
    InputLabel,
} from "@mui/material";
import DatePickerComponent from "../../Common/DatePicker";
import Select from "../../Common/Select";
import MultipleSelectJson from "../../Common/MultipleSelectJson";
import { Clear as ClearIcon } from "@mui/icons-material";

import { useGenerateOrdersReportQuery } from "../../../store/reports/reportsApis";
import { useGetRegionsDropdownQuery } from "../../../store/masters/mastersApis";
import { useGetProductsDropdownQuery } from "../../../store/masters/mastersApis";
import { useGetUsersDropdownQuery } from "../../../store/users/usersApis";

const OrdersReport = ({
    reportType,
    filters,
    onFilterUpdate,
    onFilterReset,
    onExport,
    onRefresh,
    onReportDataUpdate,
}) => {
    const [localFilters, setLocalFilters] = useState(filters);

    // Update local filters when props change
    useEffect(() => {
        setLocalFilters(filters);
    }, [filters]);

    // Fetch dropdown data
    const { data: regionsData } = useGetRegionsDropdownQuery();
    const { data: productsData } = useGetProductsDropdownQuery();
    const { data: staffData } = useGetUsersDropdownQuery({ role: "staff" });

    // Memoize staff options to prevent recreation on every render
    const staffOptions = useMemo(() => {
        return staffData?.results?.map((staff) => ({ value: staff.id, label: staff.name })) || [];
    }, [staffData]);

    // Prepare filters for API - extract IDs from objects
    const prepareFiltersForAPI = (filters) => {
        const apiFilters = { ...filters };

        // Extract IDs from array of objects
        if (Array.isArray(apiFilters.region_ids)) {
            apiFilters.region_ids = apiFilters.region_ids.map((item) => item.value || item);
        }
        if (Array.isArray(apiFilters.product_ids)) {
            apiFilters.product_ids = apiFilters.product_ids.map((item) => item.value || item);
        }
        if (Array.isArray(apiFilters.created_by_ids)) {
            apiFilters.created_by_ids = apiFilters.created_by_ids.map((item) => item.value || item);
        }

        return apiFilters;
    };

    // Generate report query - real-time filtering with prepared filters
    const {
        data: reportData,
        isLoading,
        error,
        refetch,
    } = useGenerateOrdersReportQuery(prepareFiltersForAPI(localFilters));

    // Update parent with report data whenever it changes
    useEffect(() => {
        if (reportData && onReportDataUpdate) {
            onReportDataUpdate("orders", reportData);
        }
    }, [reportData, onReportDataUpdate]);

    // Handle filter changes - store complete objects
    const handleFilterChange = (field, value) => {
        const newFilters = { ...localFilters, [field]: value };
        setLocalFilters(newFilters);
        onFilterUpdate(newFilters);
    };

    // Handle reset
    const handleReset = () => {
        const resetFilters = {
            start_date: null,
            end_date: null,
            payment_status: null,
            payment_type: null,
            region_ids: [],
            product_ids: [],
            created_by_ids: [],
            format: "json",
        };
        setLocalFilters(resetFilters);
        onFilterReset();
    };

    // Get payment status color
    const getPaymentStatusColor = (status) => {
        const colors = {
            pending: "warning",
            partial: "info",
            paid: "success",
            failed: "error",
            cancelled: "error",
            refunded: "secondary",
            initiated: "primary",
        };
        return colors[status] || "default";
    };

    return (
        <Box>
            {/* Filters */}
            <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                    Filters
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                        <DatePickerComponent
                            label="Start Date"
                            value={localFilters.start_date}
                            onChange={(date) => handleFilterChange("start_date", date)}
                            name="start_date"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <DatePickerComponent
                            label="End Date"
                            value={localFilters.end_date}
                            onChange={(date) => handleFilterChange("end_date", date)}
                            name="end_date"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Select
                            label="Payment Status"
                            value={localFilters.payment_status || ""}
                            onChange={(e) => handleFilterChange("payment_status", e.target.value)}
                            name="payment_status"
                            options={[
                                { value: "", label: "All Payment Status" },
                                { value: "partial", label: "Partial Paid" },
                                { value: "paid", label: "Full Paid" },
                            ]}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Select
                            label="Payment Type"
                            value={localFilters.payment_type || ""}
                            onChange={(e) => handleFilterChange("payment_type", e.target.value)}
                            name="payment_type"
                            options={[
                                { value: "", label: "All Payment Types" },
                                { value: "full", label: "Full Payment" },
                                { value: "half", label: "Half Payment" },
                            ]}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "6px" }}>
                            <InputLabel sx={{ fontSize: "14px", color: "#454545", fontWeight: "500" }} shrink={false}>
                                Regions
                            </InputLabel>
                            <MultipleSelectJson
                                id="region-select"
                                // label="Regions"
                                value={localFilters.region_ids || []}
                                onChange={(value) => handleFilterChange("region_ids", value)}
                                name="region_ids"
                                options={regionsData || []}
                                size="small"
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "6px" }}>
                            <InputLabel sx={{ fontSize: "14px", color: "#454545", fontWeight: "500" }} shrink={false}>
                                Products
                            </InputLabel>
                            <MultipleSelectJson
                                id="product-select"
                                // label="Products"
                                value={localFilters.product_ids || []}
                                onChange={(value) => handleFilterChange("product_ids", value)}
                                name="product_ids"
                                options={productsData || []}
                                size="small"
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "6px" }}>
                            <InputLabel sx={{ fontSize: "14px", color: "#454545", fontWeight: "500" }} shrink={false}>
                                Staff Members
                            </InputLabel>
                            <MultipleSelectJson
                                id="staff-select"
                                // label="Staff Members"
                                value={localFilters.created_by_ids || []}
                                onChange={(value) => handleFilterChange("created_by_ids", value)}
                                name="created_by_ids"
                                options={staffOptions}
                                size="small"
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Select
                            label="Format"
                            value={localFilters.format || "json"}
                            onChange={(e) => handleFilterChange("format", e.target.value)}
                            name="format"
                            options={[
                                { value: "json", label: "JSON" },
                                { value: "csv", label: "CSV" },
                                { value: "excel", label: "Excel" },
                            ]}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Stack direction="row" spacing={1}>
                            <Button variant="outlined" startIcon={<ClearIcon />} onClick={handleReset} size="small">
                                Reset
                            </Button>
                        </Stack>
                    </Grid>
                </Grid>
            </Paper>

            {/* Loading State */}
            {isLoading && (
                <Box display="flex" justifyContent="center" p={4}>
                    <CircularProgress />
                </Box>
            )}

            {/* Error State */}
            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error?.data?.message || "Failed to generate orders report"}
                </Alert>
            )}

            {/* Report Data */}
            {reportData && !isLoading && (
                <Box>
                    {/* Summary Cards */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card>
                                <CardContent>
                                    <Typography color="textSecondary" gutterBottom>
                                        Total Orders
                                    </Typography>
                                    <Typography variant="h4">{reportData.summary?.total_orders || 0}</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card>
                                <CardContent>
                                    <Typography color="textSecondary" gutterBottom>
                                        Total Amount
                                    </Typography>
                                    <Typography variant="h4">
                                        ₹{reportData.summary?.total_amount?.toLocaleString() || 0}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card>
                                <CardContent>
                                    <Typography color="textSecondary" gutterBottom>
                                        Paid Amount
                                    </Typography>
                                    <Typography variant="h4">
                                        ₹{reportData.summary?.paid_amount?.toLocaleString() || 0}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card>
                                <CardContent>
                                    <Typography color="textSecondary" gutterBottom>
                                        Pending Amount
                                    </Typography>
                                    <Typography variant="h4">
                                        ₹{reportData.summary?.pending_amount?.toLocaleString() || 0}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    {/* Breakdown Charts */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 2 }}>
                                <Typography variant="h6" gutterBottom>
                                    Payment Status Breakdown
                                </Typography>
                                {reportData.summary?.payment_status_breakdown && (
                                    <Box>
                                        {Object.entries(reportData.summary.payment_status_breakdown).map(
                                            ([status, count]) => (
                                                <Box key={status} display="flex" justifyContent="space-between" mb={1}>
                                                    <Typography variant="body2" textTransform="capitalize">
                                                        {status === "paid"
                                                            ? "Full Paid"
                                                            : status === "partial"
                                                            ? "Partial Paid"
                                                            : status}
                                                    </Typography>
                                                    <Chip
                                                        label={count}
                                                        size="small"
                                                        color={getPaymentStatusColor(status)}
                                                    />
                                                </Box>
                                            ),
                                        )}
                                    </Box>
                                )}
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 2 }}>
                                <Typography variant="h6" gutterBottom>
                                    Payment Type Breakdown
                                </Typography>
                                {reportData.summary?.payment_type_breakdown && (
                                    <Box>
                                        {Object.entries(reportData.summary.payment_type_breakdown).map(
                                            ([type, count]) => (
                                                <Box key={type} display="flex" justifyContent="space-between" mb={1}>
                                                    <Typography variant="body2" textTransform="capitalize">
                                                        {type === "full" ? "Full Payment" : "Half Payment"}
                                                    </Typography>
                                                    <Chip label={count} size="small" color="primary" />
                                                </Box>
                                            ),
                                        )}
                                    </Box>
                                )}
                            </Paper>
                        </Grid>
                    </Grid>

                    {/* Orders Table */}
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            Orders Details ({reportData.orders?.length || 0} orders)
                        </Typography>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Order Number</TableCell>
                                        <TableCell>Customer</TableCell>
                                        <TableCell>Product</TableCell>
                                        <TableCell>Amount</TableCell>
                                        <TableCell>Payment Status</TableCell>
                                        <TableCell>Payment Type</TableCell>
                                        <TableCell>Created Date</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {reportData.orders?.map((order) => (
                                        <TableRow key={order.id}>
                                            <TableCell>{order.order_number}</TableCell>
                                            <TableCell>
                                                {order.customer?.name}
                                                <br />
                                                <Typography variant="caption" color="text.secondary">
                                                    {order.customer?.mobile}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>{order.product?.name}</TableCell>
                                            <TableCell>₹{order.total_amount?.toLocaleString()}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={
                                                        order.payment_status === "paid"
                                                            ? "Full Paid"
                                                            : order.payment_status === "partial"
                                                            ? "Partial Paid"
                                                            : order.payment_status
                                                    }
                                                    size="small"
                                                    color={getPaymentStatusColor(order.payment_status)}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={
                                                        order.payment_type === "full" ? "Full Payment" : "Half Payment"
                                                    }
                                                    size="small"
                                                    color="primary"
                                                />
                                            </TableCell>
                                            <TableCell>{new Date(order.created_at).toLocaleDateString()}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Box>
            )}

            {/* No Data State */}
            {!reportData && !isLoading && !error && (
                <Alert severity="info">No orders found for the selected filters.</Alert>
            )}
        </Box>
    );
};

export default OrdersReport;
